import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Button, Modal } from '@teo/components';
import { Ai } from '@teo/components/icons';
import { ViewUplaodElement } from '@/components/ViewUplaodElement';
import { ConfirmModal } from '@/components/ConfirmModal';
import { axios } from '@/axios';
import { config } from '@/config';
import { uploadFile } from '@/query/documents';
import { newUid } from '@/util';

type AiDocumentJobMode = 'lesson' | 'course';

type AiJobMessage = {
  id: string;
  text: string;
  type?: 'info' | 'success' | 'error';
};

type UploadedDocument = {
  name: string;
  type: string;
  size: number;
  url: string;
};

type AiExamplePrompt = {
  id: string;
  type: AiDocumentJobMode;
  category: string;
  title: string;
  text: string;
  categoryTone?: 'blue' | 'green' | 'grey';
};

type AiDocumentJobModalProps = {
  open: boolean;
  mode: AiDocumentJobMode;
  onClose: () => void;
  coursecontentId?: string | number | null;
  title?: string;
  initialTitle?: string;
  description?: string;
  promptPlaceholder?: string;
  startLabel?: string;
  endpoint?: string;
  extraPayload?: Record<string, any>;
  onStarted?: (result: any) => void;
  onCompleted?: (result: any) => void;
  completedActionLabel?: string;
  examplePrompts?: AiExamplePrompt[];
  showCourseTitle?: boolean;
  showLanguageSelector?: boolean;
};

const EXAMPLE_PROMPTS: AiExamplePrompt[] = [
  {
    id: 'course-practice',
    type: 'course',
    category: 'aiDocumentJobModal.examplePrompts.coursePractice.category',
    title: 'aiDocumentJobModal.examplePrompts.coursePractice.title',
    text: 'aiDocumentJobModal.examplePrompts.coursePractice.text',
    categoryTone: 'green',
  },
  {
    id: 'course-theory',
    type: 'course',
    category: 'aiDocumentJobModal.examplePrompts.courseTheory.category',
    title: 'aiDocumentJobModal.examplePrompts.courseTheory.title',
    text: 'aiDocumentJobModal.examplePrompts.courseTheory.text',
    categoryTone: 'blue',
  },
  {
    id: 'course-technical-skills',
    type: 'course',
    category: 'aiDocumentJobModal.examplePrompts.technicalSkills.category',
    title: 'aiDocumentJobModal.examplePrompts.technicalSkills.title',
    text: 'aiDocumentJobModal.examplePrompts.technicalSkills.text',
  },
  {
    id: 'lesson-practice',
    type: 'lesson',
    category: 'aiDocumentJobModal.examplePrompts.practice.category',
    title: 'aiDocumentJobModal.examplePrompts.practice.title',
    text: 'aiDocumentJobModal.examplePrompts.practice.text',
    categoryTone: 'green',
  },
  {
    id: 'lesson-explainer',
    type: 'lesson',
    category: 'aiDocumentJobModal.examplePrompts.explainer.category',
    title: 'aiDocumentJobModal.examplePrompts.explainer.title',
    text: 'aiDocumentJobModal.examplePrompts.explainer.text',
  },
  {
    id: 'lesson-knowledge-check',
    type: 'lesson',
    category: 'aiDocumentJobModal.examplePrompts.knowledgeCheck.category',
    title: 'aiDocumentJobModal.examplePrompts.knowledgeCheck.title',
    text: 'aiDocumentJobModal.examplePrompts.knowledgeCheck.text',
  },
];

function resolveBackendUrl(url?: string | null): string | null {
  if (!url) return null;

  const raw = String(url);
  if (/^https?:\/\//i.test(raw)) return raw;

  const apiBase = String(config.backend || '').replace(/\/+$/, '');

  if (raw.startsWith('/api/')) {
    const root = apiBase.replace(/\/api\/?$/, '');
    return `${root}${raw}`;
  }

  if (raw.startsWith('/')) {
    return `${apiBase}${raw}`;
  }

  return `${apiBase}/${raw}`;
}

function getJobEventText(data: any): string {
  return (
    data?.message ||
    data?.data?.message ||
    data?.step ||
    data?.status ||
    data?.type ||
    'Job update received'
  );
}

function isFinalJobEvent(data: any): boolean {
  const type = String(data?.type || '').toLowerCase();
  const status = String(data?.status || '').toLowerCase();
  const finalStatuses = [
    'completed',
    'failed',
    'cancelled',
    'abandoned',
    'expired',
  ];

  return finalStatuses.includes(type) || finalStatuses.includes(status);
}

function getCompletedJobResult(data: any): any {
  return data?.result || data?.data?.result || data?.data || data || null;
}

function getNumericProgress(data: any): number | null {
  const candidates = [
    data?.progress,
    data?.data?.progress,
    data?.metadata?.progress,
    data?.data?.metadata?.progress,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) {
      return Math.max(0, Math.min(100, value));
    }
  }

  return null;
}

function getDefaultEndpoint(mode: AiDocumentJobMode) {
  return `${config.backend}/external/v1/instructai/documents/${mode}/start`;
}

function getFileExtension(fileName?: string | null): string {
  const name = String(fileName || '');
  const index = name.lastIndexOf('.');

  if (index < 0 || index === name.length - 1) return '';

  return name.slice(index + 1).toLowerCase();
}

const PUBLICATION_COURSE_TYPES = [
  'course',
  'leervacature',
  'assessment',
  'instruction',
] as const;

type PublicationCourseType = (typeof PUBLICATION_COURSE_TYPES)[number];

const PUBLICATION_COURSE_TYPE_EXPLANATIONS: Record<
  PublicationCourseType,
  string
> = {
  course:
    'The AI will create a normal course: structured lessons with explanations, examples, exercises and knowledge checks.',
  leervacature:
    'The AI will create a vacancy: content that explains the role, expectations, practical situations and checks for candidates or employees.',
  assessment:
    'The AI will create an assessment: focus on tasks, questions, evidence and evaluation instead of long theory.',
  instruction:
    'The AI will create an instruction: short, practical step-by-step guidance for performing a task correctly and safely.',
};

const PUBLICATION_COURSE_TYPE_TITLE_LABELS: Record<
  PublicationCourseType,
  string
> = {
  course: 'Course title',
  leervacature: 'Vacancy title',
  assessment: 'Assessment title',
  instruction: 'Instruction title',
};

const LANGUAGE_OPTIONS = ['nl', 'en', 'fr', 'de', 'es'];

const PROMPT_PLACEHOLDER_PATTERN = /(\[[^\]]+\])/g;

const PROMPT_CATEGORY_TONE_CLASSES: Record<
  NonNullable<AiExamplePrompt['categoryTone']>,
  string
> = {
  blue: 'bg-[#edf1ff] text-tint-blue',
  green: 'bg-[#e8f7ef] text-[#1f7a45]',
  grey: 'bg-[#f1f2f4] text-grey-06',
};

function getPublicationCourseType(value: any): PublicationCourseType | null {
  const publicationCourseType = String(value || '').trim();

  return PUBLICATION_COURSE_TYPES.includes(
    publicationCourseType as PublicationCourseType
  )
    ? (publicationCourseType as PublicationCourseType)
    : null;
}

function getPayloadLanguage(extraPayload: Record<string, any>) {
  return String(extraPayload?.lang || extraPayload?.language || '').trim();
}

function renderHighlightedPrompt(text: string) {
  return text.split(PROMPT_PLACEHOLDER_PATTERN).map((part, index) => {
    if (!part) return null;

    if (part.startsWith('[') && part.endsWith(']')) {
      return (
        <span
          key={`${part}_${index}`}
          className="rounded bg-[#fff3bf] px-0.5 text-[#9a6700]"
        >
          {part}
        </span>
      );
    }

    return <React.Fragment key={`${part}_${index}`}>{part}</React.Fragment>;
  });
}

function isPromptSectionHeading(line: string) {
  const trimmedLine = line.trim();

  return (
    Boolean(trimmedLine) &&
    !trimmedLine.startsWith('-') &&
    trimmedLine === trimmedLine.toUpperCase()
  );
}

function renderPromptPreview(text: string) {
  return text.split('\n').map((line, index) => {
    const trimmedLine = line.trim();
    const key = `${index}_${line}`;

    if (!trimmedLine) {
      return <div key={key} className="h-3" />;
    }

    if (isPromptSectionHeading(trimmedLine)) {
      return (
        <div
          key={key}
          className={`text-xs font-semibold uppercase tracking-wide text-[#231f20] ${
            index === 0 ? '' : 'mt-3'
          }`}
        >
          {trimmedLine}
        </div>
      );
    }

    if (trimmedLine.startsWith('-')) {
      return (
        <div key={key} className="mt-1.5 flex gap-2">
          <span className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-grey-05" />
          <span>{renderHighlightedPrompt(trimmedLine.slice(1).trim())}</span>
        </div>
      );
    }

    return (
      <p key={key} className="leading-5">
        {renderHighlightedPrompt(trimmedLine)}
      </p>
    );
  });
}

function getCollapsedPromptPreviewLines(text: string): string[] {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const intro = lines.find(
    (line) => !line.startsWith('-') && !isPromptSectionHeading(line)
  );
  const firstHeading = lines.find(isPromptSectionHeading);
  const bullets = lines.filter((line) => line.startsWith('-')).slice(0, 2);

  return [intro, firstHeading, ...bullets].filter((line): line is string =>
    Boolean(line)
  );
}

function renderCollapsedPromptPreview(text: string) {
  return getCollapsedPromptPreviewLines(text).map((line, index) => {
    const key = `${index}_${line}`;

    if (isPromptSectionHeading(line)) {
      return (
        <div
          key={key}
          className="mt-2 text-xs font-semibold uppercase tracking-wide text-[#231f20] first:mt-0"
        >
          {line}
        </div>
      );
    }

    if (line.startsWith('-')) {
      return (
        <div key={key} className="mt-1 flex gap-2">
          <span className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-grey-05" />
          <span>{renderHighlightedPrompt(line.slice(1).trim())}</span>
        </div>
      );
    }

    return (
      <p key={key} className="leading-5">
        {renderHighlightedPrompt(line)}
      </p>
    );
  });
}

export const AiDocumentJobModal = ({
  open,
  mode,
  onClose,
  coursecontentId,
  title,
  initialTitle,
  description,
  promptPlaceholder,
  startLabel,
  endpoint,
  extraPayload = {},
  onStarted,
  onCompleted,
  completedActionLabel,
  examplePrompts,
  showCourseTitle,
  showLanguageSelector,
}: AiDocumentJobModalProps) => {
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastErrorRef = useRef<any>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [prompt, setPrompt] = useState('');
  const [courseTitle, setCourseTitle] = useState('');
  const [messages, setMessages] = useState<AiJobMessage[]>([]);
  const [jobId, setJobId] = useState<string | number | null>(null);
  const [cancelUrl, setCancelUrl] = useState<string | null>(null);
  const [completedResult, setCompletedResult] = useState<any>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showPrompts, setShowPrompts] = useState(false);
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    getPayloadLanguage(extraPayload)
  );

  const isLesson = mode === 'lesson';
  const shouldShowCourseTitle = showCourseTitle ?? true;
  const shouldShowLanguageSelector = showLanguageSelector ?? true;
  const initialTitleValue = String(
    initialTitle ??
      extraPayload?.lessonTitle ??
      extraPayload?.lessonName ??
      extraPayload?.course?.title ??
      extraPayload?.course?.name ??
      extraPayload?.course?.courseName ??
      extraPayload?.title ??
      extraPayload?.name ??
      ''
  ).trim();

  const tr = (key: string, defaultValue?: string) =>
    t(key, { defaultValue: defaultValue || key });

  const publicationCourseType = !isLesson
    ? getPublicationCourseType(extraPayload?.publicationCourseType)
    : null;
  const publicationCourseTypeExplanation = publicationCourseType
    ? tr(
        `aiDocumentJobModal.publicationCourseType.${publicationCourseType}`,
        PUBLICATION_COURSE_TYPE_EXPLANATIONS[publicationCourseType]
      )
    : '';
  const courseTitleLabel = publicationCourseType
    ? tr(
        `aiDocumentJobModal.courseTitle.${publicationCourseType}.label`,
        PUBLICATION_COURSE_TYPE_TITLE_LABELS[publicationCourseType]
      )
    : tr('aiDocumentJobModal.courseTitle.label');
  const activeExamplePrompts = (examplePrompts ?? EXAMPLE_PROMPTS).filter(
    (example) => example.type === mode
  );

  const modalTitle =
    title ||
    (isLesson
      ? 'aiDocumentJobModal.lesson.title'
      : 'aiDocumentJobModal.course.title');

  const modalDescription =
    description ||
    (isLesson
      ? 'aiDocumentJobModal.lesson.description'
      : 'aiDocumentJobModal.course.description');

  const placeholder =
    promptPlaceholder ||
    (isLesson
      ? 'aiDocumentJobModal.lesson.promptPlaceholder'
      : 'aiDocumentJobModal.course.promptPlaceholder');

  const addMessage = (text: string, type: AiJobMessage['type'] = 'info') => {
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: newUid(10),
        text,
        type,
      },
    ]);
  };

  const bumpProgress = (nextProgress: number) => {
    setProgress((currentProgress) =>
      Math.max(currentProgress, Math.min(100, nextProgress))
    );
  };

  const reset = (nextInitialTitle = '') => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    lastErrorRef.current = null;
    setFiles([]);
    setPrompt('');
    setCourseTitle(nextInitialTitle);
    setMessages([]);
    setJobId(null);
    setCancelUrl(null);
    setCompletedResult(null);
    setIsStarting(false);
    setIsCancelling(false);
    setProgress(0);
    setShowPrompts(false);
    setExpandedPromptId(null);
    setErrorMessage(null);
    setShowCancelConfirm(false);
    setSelectedLanguage(getPayloadLanguage(extraPayload));
  };

  const returnToPromptStage = () => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    lastErrorRef.current = null;
    setMessages([]);
    setJobId(null);
    setCancelUrl(null);
    setCompletedResult(null);
    setIsStarting(false);
    setIsCancelling(false);
    setProgress(0);
    setShowPrompts(false);
    setErrorMessage(null);
    setShowCancelConfirm(false);
  };

  const closeModal = () => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setShowCancelConfirm(false);
    onClose();
    setIsStarting(false);
    setIsCancelling(false);
  };

  const hasInterruptedJob = () =>
    !completedResult && Boolean(errorMessage && (isStarting || jobId));

  const shouldConfirmCancel = () =>
    !completedResult && isStarting && !errorMessage;

  const handleClose = () => {
    if (hasInterruptedJob()) {
      returnToPromptStage();
      return;
    }

    if (shouldConfirmCancel()) {
      setShowCancelConfirm(true);
      return;
    }

    closeModal();
  };

  const getResolvedCancelUrl = () => {
    if (cancelUrl) return cancelUrl;
    if (!jobId) return null;

    return resolveBackendUrl(`/jobqueue/${jobId}/cancel`);
  };

  const cancelRunningJob = async () => {
    const resolvedCancelUrl = getResolvedCancelUrl();

    if (!resolvedCancelUrl) return true;

    setIsCancelling(true);

    try {
      await axios.post(resolvedCancelUrl, {
        message: 'Job cancelled by user',
      });

      addMessage(tr('aiDocumentJobModal.cancel.cancelled'), 'success');

      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        tr('aiDocumentJobModal.cancel.failed');

      reportError(
        status === 409 ? tr('aiDocumentJobModal.cancel.tooLate') : message,
        err
      );

      return false;
    } finally {
      setIsCancelling(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (isCancelling) return;

    const shouldCancelJob = Boolean(jobId && isStarting && !completedResult);

    if (shouldCancelJob) {
      const cancelled = await cancelRunningJob();
      if (!cancelled) return;
    }

    closeModal();
  };

  useEffect(() => {
    if (!open) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      return;
    }

    reset(initialTitleValue);

    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
    // reset is intentionally not included: it is recreated on every render.
    // The actual title value used for reset is tracked explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTitleValue]);

  const reportError = (message: string, error?: any) => {
    lastErrorRef.current = error || message;
    setErrorMessage(message);
    addMessage(message, 'error');
    console.error(`[AiDocumentJobModal] ${message}`, error || '');
  };

  const logLastErrorToConsole = () => {
    console.error(
      '[AiDocumentJobModal] Last AI generation error',
      lastErrorRef.current
    );
  };

  const connectToJob = (sseUrl: string) => {
    eventSourceRef.current?.close();

    const source = new EventSource(sseUrl, {
      withCredentials: true,
    });

    eventSourceRef.current = source;

    const handleEvent = (event: Event) => {
      const messageEvent = event as MessageEvent;
      let data: any = {};

      try {
        data = JSON.parse(messageEvent.data);
      } catch {
        data = {
          type: messageEvent.type,
          message: messageEvent.data,
        };
      }

      const eventProgress = getNumericProgress(data);
      if (eventProgress != null) {
        bumpProgress(eventProgress);
      } else if (messageEvent.type === 'queued') {
        bumpProgress(20);
      } else if (messageEvent.type === 'started') {
        bumpProgress(30);
      } else if (
        messageEvent.type === 'progress' ||
        messageEvent.type === 'snapshot'
      ) {
        setProgress((currentProgress) => Math.min(92, currentProgress + 8));
      }

      addMessage(getJobEventText(data));

      if (isFinalJobEvent(data)) {
        setIsStarting(false);
        source.close();

        const type = String(data?.type || '').toLowerCase();
        const status = String(data?.status || '').toLowerCase();

        if (type === 'completed' || status === 'completed') {
          bumpProgress(100);
          setCompletedResult(getCompletedJobResult(data));
          return;
        }

        reportError(getJobEventText(data), data);
      }
    };

    [
      'connected',
      'snapshot',
      'queued',
      'started',
      'progress',
      'completed',
      'failed',
      'cancelled',
      'abandoned',
      'expired',
    ].forEach((eventName) => {
      source.addEventListener(eventName, handleEvent);
    });

    let reportedConnectionError = false;

    source.onerror = (error) => {
      if (reportedConnectionError) return;

      reportedConnectionError = true;

      reportError(tr('aiDocumentJobModal.error.jobTrackerInterrupted'), error);
    };
  };

  const uploadDocuments = async (): Promise<UploadedDocument[]> => {
    if (!files.length) return [];

    addMessage(tr('aiDocumentJobModal.status.uploadingDocuments'));
    bumpProgress(5);

    const documents: UploadedDocument[] = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];

      addMessage(
        tr('aiDocumentJobModal.status.uploadingDocument') +
          ` ${i + 1}/${files.length}: ${file.name}`
      );
      bumpProgress(5 + Math.round(((i + 1) / files.length) * 15));

      const url = await uploadFile('upload/', file);

      documents.push({
        name: file.name,
        type: file.type,
        size: file.size,
        url,
      });
    }

    return documents;
  };

  const startJob = async () => {
    if (isStarting) return;

    const cleanPrompt = prompt.trim();
    const cleanTitle = courseTitle.trim();

    if (!cleanPrompt && !files.length) {
      reportError(tr('aiDocumentJobModal.error.promptOrDocumentRequired'));
      return;
    }

    setIsStarting(true);
    setMessages([]);
    setJobId(null);
    setCompletedResult(null);
    setErrorMessage(null);
    lastErrorRef.current = null;
    setProgress(2);

    try {
      const documents = await uploadDocuments();

      addMessage(
        isLesson
          ? tr('aiDocumentJobModal.status.startingLesson')
          : tr('aiDocumentJobModal.status.startingCourse')
      );
      bumpProgress(22);

      const titlePayload = cleanTitle
        ? isLesson
          ? {
              title: cleanTitle,
              name: cleanTitle,
              lessonTitle: cleanTitle,
              lessonName: cleanTitle,
            }
          : {
              title: cleanTitle,
              name: cleanTitle,
              courseName: cleanTitle,
            }
        : {};

      const promptWithPublicationType = [
        publicationCourseType
          ? `Publication type: ${publicationCourseType}\n${publicationCourseTypeExplanation}`
          : '',
        cleanPrompt,
      ]
        .filter(Boolean)
        .join('\n\n');

      const payload = {
        ...extraPayload,
        ...titlePayload,
        ...(isLesson && coursecontentId != null
          ? { coursecontentId: parseInt(String(coursecontentId), 10) }
          : {}),
        ...(publicationCourseType
          ? {
              publicationCourseType,
              publicationCourseTypeExplanation,
            }
          : {}),
        ...(selectedLanguage ? { lang: selectedLanguage } : {}),
        prompt: promptWithPublicationType || cleanPrompt,
        documents,
      };

      const { data: result } = await axios.post(
        endpoint || getDefaultEndpoint(mode),
        payload
      );

      const resultJobId =
        result?.jobId || result?.job?.id || result?.id || null;
      const sseUrl = resolveBackendUrl(
        result?.sseUrl ||
          (resultJobId ? `/jobqueue/${resultJobId}/events` : null)
      );
      const resultCancelUrl = resolveBackendUrl(
        result?.cancelUrl ||
          (resultJobId ? `/jobqueue/${resultJobId}/cancel` : null)
      );

      setJobId(resultJobId);
      setCancelUrl(resultCancelUrl);
      addMessage(
        resultJobId
          ? `AI ${mode} job started: ${resultJobId}`
          : `AI ${mode} job started.`,
        'success'
      );
      bumpProgress(28);

      onStarted?.(result);

      if (sseUrl) {
        connectToJob(sseUrl);
      } else {
        reportError(tr('aiDocumentJobModal.error.noTrackingUrl'), result);
        setIsStarting(false);
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        tr('aiDocumentJobModal.error.startFailed');

      reportError(message, err);
      setIsStarting(false);
    }
  };

  const handleCompletedAction = () => {
    if (completedResult) {
      onCompleted?.(completedResult);
    }

    setShowCancelConfirm(false);
    onClose();
  };

  if (!open) return null;

  const isRunning = isStarting && !completedResult;
  const isComplete = Boolean(completedResult);
  const isProgressState = isRunning || isComplete;
  const canShowPromptPanel = !isProgressState;
  const hasExamplePrompts = activeExamplePrompts.length > 0;
  const promptPanelVisible =
    canShowPromptPanel && hasExamplePrompts && showPrompts;
  const progressCircleRadius = 45;
  const progressCircleCircumference = 2 * Math.PI * progressCircleRadius;
  const progressCircleOffset =
    progressCircleCircumference -
    (progressCircleCircumference * progress) / 100;

  return (
    <>
      <style>
        {`
          .ReactModal__Overlay:has(.ai-document-job-prompts-modal) {
            background: transparent !important;
            pointer-events: none;
          }

          .ai-document-job-main-modal,
          .ai-document-job-prompts-modal {
            --ai-main-modal-width: 800px;
            --ai-prompts-modal-width: 390px;
            --ai-prompts-gap: 10px;
            --ai-group-left: calc(
              (100vw - var(--ai-main-modal-width) - var(--ai-prompts-gap) - var(--ai-prompts-modal-width)) / 2
            );

            position: fixed !important;
            top: 2rem !important;
            margin: 0 !important;
          }

          .ai-document-job-main-modal {
            left: max(1rem, var(--ai-group-left)) !important;
            width: var(--ai-main-modal-width);
            max-width: calc(100vw - 2rem);
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - 4rem);
          }

          .ai-document-job-main-modal #modal-body-container {
            min-height: 0;
            overflow-y: auto;
            padding: 1.25rem 1.5rem;
            position: relative;
          }

          .ai-document-job-main-modal #modal-body-container + div {
            padding: 1rem 1.5rem;
          }

          .ai-document-job-prompts-modal {
            left: calc(
              max(1rem, var(--ai-group-left)) + var(--ai-main-modal-width) + var(--ai-prompts-gap)
            ) !important;
            width: var(--ai-prompts-modal-width);
            max-width: calc(100vw - 2rem);
            pointer-events: auto;
          }

          @media (max-width: 1220px) {
            .ai-document-job-main-modal,
            .ai-document-job-prompts-modal {
              --ai-main-modal-width: calc(100vw - var(--ai-prompts-modal-width) - var(--ai-prompts-gap) - 2rem);
              --ai-group-left: 1rem;
            }
          }

          @media (max-width: 900px) {
            .ai-document-job-main-modal,
            .ai-document-job-prompts-modal {
              --ai-main-modal-width: calc(100vw - 2rem);
              --ai-prompts-modal-width: calc(100vw - 2rem);
            }

            .ai-document-job-prompts-modal {
              left: 1rem !important;
              right: 1rem !important;
            }
          }
        `}
      </style>

      <Modal isOpen className="ai-document-job-main-modal">
        <Modal.Header onClose={handleClose}>
          <div className="flex items-center gap-2">
            <Ai className="h-5 w-5 text-tint-blue" />
            <span>{tr(modalTitle)}</span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="flex flex-col gap-4 pb-3 pr-1">
            {!isProgressState && shouldShowCourseTitle && (
              <div
                className={clsx(
                  'grid gap-3',
                  !isLesson && shouldShowLanguageSelector && 'sm:grid-cols-2'
                )}
              >
                <div className={isLesson ? 'sm:col-span-2' : ''}>
                  <label className="mb-2 block text-sm font-medium text-[#231f20]">
                    {isLesson
                      ? tr('aiDocumentJobModal.lessonTitle.label')
                      : courseTitleLabel}
                  </label>
                  <input
                    value={courseTitle}
                    onChange={(event) => setCourseTitle(event.target.value)}
                    placeholder={
                      isLesson
                        ? tr('aiDocumentJobModal.lessonTitle.placeholder')
                        : tr('aiDocumentJobModal.courseTitle.placeholder')
                    }
                    className="w-full rounded-md border border-grey-02 p-3 text-sm outline-none focus:border-tint-blue"
                  />
                  <p className="mt-2 text-xs text-grey-06">
                    {isLesson
                      ? tr('aiDocumentJobModal.lessonTitle.help')
                      : tr('aiDocumentJobModal.courseTitle.help')}
                  </p>
                </div>

                {!isLesson && shouldShowLanguageSelector && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#231f20]">
                      {tr('aiDocumentJobModal.language.label')}
                    </label>
                    <select
                      value={selectedLanguage}
                      onChange={(event) =>
                        setSelectedLanguage(event.target.value)
                      }
                      className="w-full rounded-md border border-grey-02 bg-white p-3 text-sm outline-none focus:border-tint-blue"
                    >
                      <option value="">
                        {tr('aiDocumentJobModal.language.placeholder')}
                      </option>
                      {LANGUAGE_OPTIONS.map((language) => (
                        <option key={language} value={language}>
                          {tr(`langs.${language}`, language)}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-grey-06">
                      {tr('aiDocumentJobModal.language.help')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {!isProgressState && (
              <div>
                <p className="text-sm leading-6 text-[#231f20]">
                  {tr(modalDescription)}
                </p>
                <p className="mt-1 text-sm text-grey-06">
                  {tr(
                    isLesson
                      ? 'aiDocumentJobModal.description.helpLesson'
                      : 'aiDocumentJobModal.description.help'
                  )}
                </p>
                {publicationCourseTypeExplanation && (
                  <p className="mt-1 text-sm text-grey-06">
                    {publicationCourseTypeExplanation}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-[#231f20]">
                {tr('aiDocumentJobModal.documents.label')}
              </label>

              <div
                className={`flex flex-wrap gap-3 rounded-md p-1 ${
                  files.length > 3
                    ? 'max-h-[150px] overflow-y-auto border border-grey-02 bg-[#f8f8f9]'
                    : ''
                }`}
              >
                {files.map((file, index) => {
                  const type_txt = getFileExtension(file?.name);

                  return (
                    <ViewUplaodElement
                      key={`${file.name}_${file.size}_${index}`}
                      answered={isProgressState}
                      el={file}
                      type_txt={type_txt}
                      onDelete={() => {
                        setFiles((currentFiles) =>
                          currentFiles.filter(
                            (_, fileIndex) => fileIndex !== index
                          )
                        );
                      }}
                    />
                  );
                })}
              </div>

              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept=".pdf,.doc,.docx,.txt,.md,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                className="hidden"
                onChange={(event) => {
                  const selectedFiles = Array.from(event.target.files || []);
                  setFiles((currentFiles) => [
                    ...currentFiles,
                    ...selectedFiles,
                  ]);
                  event.target.value = '';
                }}
              />

              <Button
                variant="outline"
                className="mt-2"
                disabled={isProgressState}
                onClick={() => fileInputRef.current?.click()}
              >
                {tr('aiDocumentJobModal.documents.add')}
              </Button>
            </div>

            {!isProgressState && (
              <div className="flex items-center py-0">
                <div className="h-px flex-1 bg-grey-02" />
                <span className="mx-3 rounded-full border border-grey-02 bg-grey-01 px-3 py-0.5 text-xs font-semibold tracking-wide text-grey-06">
                  {tr('aiDocumentJobModal.inputDivider.andOr')}
                </span>
                <div className="h-px flex-1 bg-grey-02" />
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-[#231f20]">
                {tr('aiDocumentJobModal.prompt.label')}
              </label>
              <div className="relative leading-none">
                <textarea
                  value={prompt}
                  disabled={isProgressState}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder={tr(placeholder)}
                  className="relative block max-h-[300px] min-h-[150px] w-full resize-y rounded-md border border-grey-02 bg-white p-3 text-sm leading-5 text-[#231f20] outline-none focus:border-tint-blue disabled:bg-[#f8f8f9] sm:min-h-[180px]"
                />
              </div>

              {!isProgressState && hasExamplePrompts && (
                <Button
                  variant="outline"
                  className="mt-3"
                  aria-expanded={showPrompts}
                  onClick={() =>
                    setShowPrompts((currentValue) => !currentValue)
                  }
                >
                  {showPrompts
                    ? tr('aiDocumentJobModal.examplePrompts.hide')
                    : tr('aiDocumentJobModal.examplePrompts.open')}
                </Button>
              )}
            </div>

            {isProgressState && (
              <>
                <div className="rounded-md border border-[#c9d5ff] bg-[#edf1ff] p-4 text-sm text-[#231f20]">
                  {isComplete
                    ? tr(
                        isLesson
                          ? 'aiDocumentJobModal.complete.noticeLesson'
                          : 'aiDocumentJobModal.complete.notice'
                      )
                    : tr(
                        isLesson
                          ? 'aiDocumentJobModal.running.noticeLesson'
                          : 'aiDocumentJobModal.running.notice'
                      )}
                </div>

                <div className="rounded-md border border-grey-02 bg-[#f8f8f9] px-4 py-9 text-center">
                  <div className="relative mx-auto mb-4 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#eef2ff]">
                    <svg
                      className="h-[102px] w-[102px] -rotate-90"
                      viewBox="0 0 110 110"
                    >
                      <circle
                        cx="55"
                        cy="55"
                        r={progressCircleRadius}
                        fill="none"
                        stroke="#dfe5ff"
                        strokeWidth="4"
                      />
                      <circle
                        cx="55"
                        cy="55"
                        r={progressCircleRadius}
                        fill="none"
                        stroke="#3158ff"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={progressCircleCircumference}
                        strokeDashoffset={progressCircleOffset}
                        className="transition-all duration-500 ease-out"
                      />
                    </svg>
                    <div className="absolute flex h-12 w-12 items-center justify-center rounded-full bg-white text-tint-blue">
                      <Ai className="h-7 w-7" />
                    </div>
                  </div>
                  <div className="font-semibold text-[#231f20]">
                    {isComplete
                      ? tr(
                          isLesson
                            ? 'aiDocumentJobModal.complete.titleLesson'
                            : 'aiDocumentJobModal.complete.title'
                        )
                      : tr(
                          isLesson
                            ? 'aiDocumentJobModal.running.titleLesson'
                            : 'aiDocumentJobModal.running.title'
                        )}
                    {!isComplete && (
                      <span className="ml-1 text-[#b7c4ff]">•••</span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-grey-06">
                    {isComplete
                      ? tr('aiDocumentJobModal.complete.status')
                      : `${Math.round(progress)}%`}
                  </div>
                </div>
              </>
            )}

            {errorMessage && (
              <div className="rounded-md border border-grey-02 bg-[#fff5f5] p-3 text-sm text-error-05">
                <div className="font-medium">
                  {tr('aiDocumentJobModal.error.title')}
                </div>
                <div className="mt-1">{errorMessage}</div>
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={logLastErrorToConsole}
                >
                  {tr('aiDocumentJobModal.error.logToConsole')}
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex w-full justify-between gap-3">
            <Button
              variant="ghost"
              disabled={isCancelling}
              onClick={handleClose}
            >
              {isCancelling
                ? tr('aiDocumentJobModal.cancel.cancelling')
                : completedResult
                ? tr('aiDocumentJobModal.close')
                : tr('aiDocumentJobModal.cancel.label')}
            </Button>

            {completedResult ? (
              <Button onClick={handleCompletedAction}>
                {completedActionLabel ||
                  (isLesson
                    ? tr('aiDocumentJobModal.lesson.goToLesson')
                    : tr('aiDocumentJobModal.course.viewResult'))}
              </Button>
            ) : isRunning ? (
              <Button
                variant="outline"
                disabled={isCancelling}
                onClick={closeModal}
              >
                {tr(
                  isLesson
                    ? 'aiDocumentJobModal.running.closeWindowLesson'
                    : 'aiDocumentJobModal.running.closeWindow'
                )}
              </Button>
            ) : (
              <Button
                disabled={isProgressState || isCancelling}
                onClick={startJob}
              >
                {isStarting
                  ? tr('aiDocumentJobModal.starting')
                  : startLabel || tr('aiDocumentJobModal.start')}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {promptPanelVisible && (
        <Modal
          isOpen
          className="ai-document-job-prompts-modal rounded-xl outline-none"
        >
          <Modal.Header onClose={() => setShowPrompts(false)}>
            <div className="flex items-start gap-2">
              <Ai className="mt-0.5 h-5 w-5 text-tint-blue" />
              <div>
                <div className="font-semibold text-[#231f20]">
                  {tr('aiDocumentJobModal.examplePrompts.title')}
                </div>
                <p className="mt-1 text-sm font-normal text-grey-06">
                  {tr('aiDocumentJobModal.examplePrompts.description')}
                </p>
              </div>
            </div>
          </Modal.Header>

          <div className="max-h-[calc(90vh-82px)] overflow-auto p-3">
            {activeExamplePrompts.map((example) => {
              const promptText = t(example.text);
              const promptTitle = t(example.title);
              const categoryTone = example.categoryTone || 'blue';
              const isExpanded = expandedPromptId === example.id;

              return (
                <div
                  key={example.id}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  className="mb-3 cursor-pointer rounded-md border border-grey-02 bg-white p-3 shadow-sm transition last:mb-0 hover:border-tint-blue"
                  onClick={() =>
                    setExpandedPromptId((currentPromptId) =>
                      currentPromptId === example.id ? null : example.id
                    )
                  }
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;

                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setExpandedPromptId((currentPromptId) =>
                        currentPromptId === example.id ? null : example.id
                      );
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div
                        className={`mb-2 inline-flex rounded px-2 py-1 text-xs font-semibold ${PROMPT_CATEGORY_TONE_CLASSES[categoryTone]}`}
                      >
                        {t(example.category)}
                      </div>
                      <div className="font-semibold text-[#231f20]">
                        {promptTitle}
                      </div>
                    </div>
                    <span className="mt-1 text-lg leading-none text-grey-05">
                      {isExpanded ? '-' : '+'}
                    </span>
                  </div>
                  <div className="mt-2 rounded border border-grey-02 bg-[#f8f8f9] p-2.5 text-sm leading-5 text-[#4d4d50]">
                    {isExpanded
                      ? renderPromptPreview(promptText)
                      : renderCollapsedPromptPreview(promptText)}
                  </div>
                  <div className="mt-2 text-xs font-medium text-tint-blue">
                    {tr(
                      isExpanded
                        ? 'aiDocumentJobModal.examplePrompts.collapse'
                        : 'aiDocumentJobModal.examplePrompts.viewFull'
                    )}
                  </div>
                  <Button
                    variant="outline"
                    className="mt-2 flex w-full items-center justify-center gap-2"
                    onClick={(event) => {
                      event.stopPropagation();
                      setPrompt(promptText);
                      setCourseTitle((currentTitle) =>
                        currentTitle.trim() ? currentTitle : promptTitle
                      );
                      setShowPrompts(false);
                    }}
                  >
                    <img
                      src="/images/icons/copy.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4"
                    />
                    <span>{tr('aiDocumentJobModal.examplePrompts.use')}</span>
                  </Button>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      <ConfirmModal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleConfirmCancel}
        title="pages.ai_document_job_modal.cancel_confirm_title"
        description="pages.ai_document_job_modal.cancel_confirm_description"
        cancelLabel="forms.no"
        confirmLabel="forms.yes"
      />
    </>
  );
};

export default AiDocumentJobModal;
